import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import get from 'lodash/get';
import { hooks } from '@lifesize/clients.sdk';
import { pstnNumbersSelectors } from '@lifesize/nucleus';
import { colors } from '@lifesize/clients.mui-components';
import { makeStyles, Theme, Typography } from '@material-ui/core';
import RingTone from 'components/Ringer/RingTone';
import MicrophoneLevel from 'components/AvSettings/MicrophoneLevel';
import DeviceMute, { muteType } from 'components/AvSettings/DeviceMute';
import SettingDropdown from 'components/AvSettings/SettingDropdown';
import Radiobutton from 'components/Controls/Radio';
import PSTNContainer from 'containers/PSTN/PSTNContainer';
import { AudioType, getDevicesByKind, setAudioOutput } from 'utils/deviceUtils';

const messages = defineMessages({
  header: { id: 'components.avSettings.sectionHeader.AudioSettings', defaultMessage: 'Audio Settings' },
  inputMicrophone: { id: 'components.avSettings.inputLabel.Microphone', defaultMessage: 'Microphone' },
  inputMicrophoneSelect: {
    id: 'components.avSettings.inputLabel.MicrophoneSelect',
    defaultMessage: 'Select your microphone'
  },
  inputSpeakers: { id: 'components.avSettings.inputLabel.Speakers', defaultMessage: 'Speakers' },
  inputSpeakersSelect: {
    id: 'components.avSettings.inputLabel.SpeakersSelect',
    defaultMessage: 'Select your speakers'
  },
  tabDevice: { id: 'components.avSettings.tabLabel.DeviceAudio', defaultMessage: 'Device Audio' },
  tabPhone: { id: 'components.avSettings.tabLabel.Phone', defaultMessage: 'Phone' }
});

let speakerTimeout: ReturnType<typeof setTimeout>;

interface Props {
  extension?: string;
  handleConfigChange: Function;
  intl: { formatMessage: Function };
  isInCall?: boolean;
  isJoinFlowCall?: boolean;
}

export const AudioSettings = (props: Props) => {
  const { extension, handleConfigChange, intl, isInCall, isJoinFlowCall } = props;
  const classes = useStyles({});
  const [isTestingSpeakers, setIsTestingSpeakers] = useState(false);
  const devices = hooks.useDevices();
  const audioInputDevices = useMemo(() => getDevicesByKind(devices, 'audioinput'), [devices]);
  const audioOutputDevices = useMemo(() => getDevicesByKind(devices, 'audiooutput'), [devices]);
  const mediaSettings = useSelector((state) => get(state, 'app.mediaSettings'));
  const { audioDevice, localAudioMuted } = hooks.useMedia();
  const pstnNumbers = useSelector((state) => pstnNumbersSelectors.pstnNumbersWithCountryName(state));
  const showPSTNOption = !!pstnNumbers.length && extension && isJoinFlowCall;

  const handleAudioTypeChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    handleConfigChange('audio', get(event, 'target.value'));
  };

  const cancelSpeakerTest = useCallback(() => {
    clearTimeout(speakerTimeout);
    setIsTestingSpeakers(false);
  }, []);

  const handleSpeakerTest = useCallback(() => {
    clearTimeout(speakerTimeout);
    setIsTestingSpeakers(!isTestingSpeakers);
    if (!isTestingSpeakers) {
      speakerTimeout = setTimeout(() => {
        setIsTestingSpeakers(false);
      }, 4600);
    }
  }, [isTestingSpeakers]);

  const getDeviceValue = useCallback(() => {
    const selectedDevice =
      audioInputDevices.find((device) => device.value === audioDevice?.deviceId) || audioInputDevices?.[0];
    return selectedDevice?.value;
  }, [audioInputDevices, audioDevice]);

  return (
    <section className={classes.container}>
      {/* <Typography component="h6" className={classes.heading}>
        {intl.formatMessage(messages.header)}
      </Typography> */}
      {showPSTNOption && (
        <section className={classes.audioOptions} style={{ flexDirection: 'row' }}>
          <Radiobutton
            data-testid={`computer-audio-radiobutton`}
            input={{
              checked: mediaSettings.audio === AudioType.computer,
              name: 'audioType',
              onChange: handleAudioTypeChange,
              value: AudioType.computer
            }}
            label={intl.formatMessage(messages.tabDevice)}
          />
          <Radiobutton
            data-testid={`phone-audio-radiobutton`}
            input={{
              checked: mediaSettings.audio === AudioType.phone,
              name: 'audioType',
              onChange: handleAudioTypeChange,
              value: AudioType.phone
            }}
            label={intl.formatMessage(messages.tabPhone)}
          />
        </section>
      )}
      {showPSTNOption && mediaSettings.audio === 'phone' ? (
        <section>
          <PSTNContainer extension={extension} isInCall={isInCall} isJoinFlowCall={isJoinFlowCall} />
        </section>
      ) : (
        <>
          <section>
            <Typography component="h6" className={classes.subHeading} data-testid="cameraSelectionHeader">
              {intl.formatMessage(isJoinFlowCall ? messages.inputMicrophoneSelect : messages.inputMicrophone)}
            </Typography>
            <SettingDropdown
              handleChange={(value) => {
                cancelSpeakerTest();
                handleConfigChange('microphone', value);
              }}
              items={audioInputDevices}
              label={intl.formatMessage(messages.inputMicrophone)}
              testIdPrefix="microphone"
              value={getDeviceValue()}
            />
          </section>
          {!isInCall && (
            <section>
              <DeviceMute
                handleChange={() => {
                  cancelSpeakerTest();
                  handleConfigChange('localAudioMuted', !mediaSettings.localAudioMuted);
                }}
                isInCall={isInCall}
                isJoinFlowCall={isJoinFlowCall}
                isMuted={mediaSettings.localAudioMuted}
                type={muteType.mic}
              />
            </section>
          )}
          <section>
            <MicrophoneLevel
              microphoneEnabled={isInCall ? !localAudioMuted : !mediaSettings.localAudioMuted}
              microphoneDeviceId={getDeviceValue()}
            />
          </section>
          <section>
            <Typography component="h6" className={classes.subHeading} data-testid="cameraSelectionHeader">
              {intl.formatMessage(isJoinFlowCall ? messages.inputSpeakersSelect : messages.inputSpeakers)}
            </Typography>
            <SettingDropdown
              handleChange={(value) => {
                cancelSpeakerTest();
                handleConfigChange('speakers', value);
                setAudioOutput(value);
              }}
              items={audioOutputDevices}
              label={intl.formatMessage(messages.inputSpeakers)}
              testIdPrefix="speakers"
              value={
                (
                  audioOutputDevices.find((device) => device.value === mediaSettings.speakers) ||
                  audioOutputDevices?.[0]
                )?.value
              }
            />
            {!isInCall && (
              <button className={classes.speakerTestBtn} onClick={handleSpeakerTest} data-testid="speaker-test-link">
                <FormattedMessage id="components.avSettingsModal.playTest" defaultMessage="Test my speakers" />
              </button>
            )}
            {!!audioOutputDevices.length && !isInCall && isTestingSpeakers && (
              <RingTone deviceId={mediaSettings.speakers} />
            )}
          </section>
        </>
      )}
    </section>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    '& > section': {
      display: 'flex',
      flexDirection: 'column',

      '&:not(:first-child)': {
        marginTop: theme.spacing(2)
      }
    }
  },
  heading: {
    fontSize: '1.6rem'
  },
  subHeading: {
    fontSize: '1.2rem'
  },
  speakerTestBtn: {
    alignSelf: 'flex-end',
    color: colors.lifesize,
    border: 'none',
    marginTop: theme.spacing(0.5),
    outline: 'none',
    paddingRight: 0,
    textAlign: 'right'
  },
  audioOptions: {
    justifyContent: 'space-between'
  }
}));

export default injectIntl(AudioSettings);
